import { Location } from 'history'
import { peopleMembersUrl } from 'src/navigation/urls'
import { LocationState } from 'src/redux/createRootReducer'

const BULK_INVITE_QUERY_PARAM = 'bulkInvite'

type BulkInviteLocationInput = Pick<
  Location<unknown>,
  'hash' | 'search' | 'state'
> & {
  // The page the modal is opened over (Members or Invited). Optional so the
  // helper still has a sensible default when called without a location.
  pathname?: string
}

// The bulk-invite modal is deep-linked via a query param but layers over the
// live People page — it is NOT a separate page. Navigations that add/remove the
// param go through react-router (a connected-react-router LOCATION_CHANGE), and
// the location-change middleware resets a page's background Redux state whenever
// `state.backgroundLocation.pathname` differs from the current background page
// (see configureStore + createBackgroundReducer). Without a matching
// backgroundLocation, opening the modal looks like leaving the page, so that
// table's list is wiped and it comes back empty. Tagging the *origin* page as
// the modal's background keeps that comparison equal, so whichever tab you
// opened from (Members or Invited) keeps its state and you return to it.
const peopleBackgroundLocation = (
  pathname: string,
  searchWithoutFlag: string,
  hash: string,
): Location<LocationState> => ({
  pathname,
  search: searchWithoutFlag ? `?${searchWithoutFlag}` : '',
  hash,
  state: {},
})

export const bulkInvitePeopleLocation = (
  location?: BulkInviteLocationInput,
): Location<LocationState> => {
  const pathname = location?.pathname || peopleMembersUrl()
  const searchParams = new URLSearchParams(location?.search || '')
  searchParams.delete(BULK_INVITE_QUERY_PARAM)
  const backgroundSearch = searchParams.toString()
  searchParams.set(BULK_INVITE_QUERY_PARAM, '1')

  return {
    pathname,
    search: `?${searchParams.toString()}`,
    hash: location?.hash || '',
    state: {
      ...(location?.state as LocationState | undefined),
      backgroundLocation: peopleBackgroundLocation(
        pathname,
        backgroundSearch,
        location?.hash || '',
      ),
    },
  }
}

export const removeBulkInviteQueryParam = (
  location: Location<unknown>,
): Location<LocationState> => {
  const searchParams = new URLSearchParams(location.search)
  searchParams.delete(BULK_INVITE_QUERY_PARAM)

  const search = searchParams.toString()

  return {
    ...location,
    search: search ? `?${search}` : '',
    // Keep the origin page tagged as the background on close too, so closing the
    // modal returns there without triggering a background-state reset.
    state: {
      ...(location.state as LocationState | undefined),
      backgroundLocation: peopleBackgroundLocation(
        location.pathname,
        search,
        location.hash || '',
      ),
    },
  }
}
